import { useState } from 'react';
import PropTypes from 'prop-types';
import { Form, Input, Spin } from 'antd';
import { IdcardOutlined } from '@ant-design/icons';
import { sugerirRecepcionista } from 'api/recepcionistas';

/**
 * Campo del RUT de quien recibe el servicio y firma.
 *
 * Al salir del campo, busca si esa persona ya firmó antes **en ese mismo cliente** y,
 * si la encuentra, propone su nombre y su cargo.
 *
 * Dos reglas que no se pueden cambiar sin romper el cumplimiento:
 *
 * - **La sugerencia nunca pisa lo que la persona escribió.** Solo completa los campos
 *   que están vacíos. Si el técnico ya escribió un nombre, se respeta.
 * - **Lo sugerido queda editable.** Si el dato viene mal de antes (alguien tipeó mal
 *   el RUT y arrastró el error), el técnico lo corrige acá mismo.
 *
 * Si esa persona no firmó antes en ese cliente, no pasa nada: el técnico escribe el
 * nombre, como hasta hoy.
 */
// Los valores por defecto van en la desestructuración, NO en `defaultProps`: React 19
// ya no aplica defaultProps en componentes de función. Con defaultProps, `campoNombre`
// llegaba `undefined`, y `getFieldValue(undefined)` devuelve el objeto entero del
// formulario — que es truthy — así que el componente se salía por el early return y
// no sugería nunca. En silencio. Lo encontró un test, no un usuario.
const RutRecepcionistaField = ({
  form,
  idCliente = undefined,
  campoNombre = 'recepcionist_name',
  campoCargo = 'recepcionist_charge',
  requerido = false,
}) => {
  const [buscando, setBuscando] = useState(false);

  const buscar = async (event) => {
    const rut = (event.target.value || '').trim();
    if (!rut || !idCliente) return;

    // No pisar lo que el técnico ya escribió.
    const nombreActual = form.getFieldValue(campoNombre);
    if (nombreActual) return;

    setBuscando(true);
    try {
      const sugerencia = await sugerirRecepcionista(rut, idCliente);
      if (!sugerencia) return;

      const valores = { [campoNombre]: sugerencia.nombre };
      if (campoCargo && !form.getFieldValue(campoCargo) && sugerencia.cargo) {
        valores[campoCargo] = sugerencia.cargo;
      }
      form.setFieldsValue(valores);
    } catch {
      // Si la sugerencia falla, no se interrumpe la firma: el técnico escribe el
      // nombre a mano. Nunca bloquear una visita en terreno por esto.
    } finally {
      setBuscando(false);
    }
  };

  return (
    <Form.Item
      name="recepcionist_rut"
      label={
        <span className="flex items-center space-x-1">
          <IdcardOutlined className="text-indigo-600" />
          <span className="font-medium">RUT de quien recibe</span>
        </span>
      }
      rules={
        requerido
          ? [{ required: true, message: 'Ingresa el RUT de quien recibe el servicio' }]
          : undefined
      }
      extra="Si ya recibió una visita en este cliente, completamos su nombre."
    >
      <Input
        placeholder="12345678-9"
        className="rounded-lg"
        onBlur={buscar}
        suffix={buscando ? <Spin size="small" /> : null}
      />
    </Form.Item>
  );
};

RutRecepcionistaField.propTypes = {
  form: PropTypes.object.isRequired,
  /** Cliente de la visita. Sin él no hay sugerencia: es el que acota la búsqueda. */
  idCliente: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  campoNombre: PropTypes.string,
  campoCargo: PropTypes.string,
  requerido: PropTypes.bool,
};

export default RutRecepcionistaField;
